/**
 * BlockEditor Component
 * 
 * Unified block-based content editor for scanned books.
 * Replaces the fragmented textarea-based editor with:
 * - Drag-reorderable digital page blocks
 * - Rich text formatting (indent, bold, italic)
 * - Image region selection for instant re-OCR
 * - Live preview
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  ArrowLeft,
  Save,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  Plus,
  Scan,
  CheckCircle,
  ZoomIn,
  ZoomOut,
  Paintbrush,
  RotateCcw,
  RotateCw,
  Sparkles,
} from 'lucide-react';
import * as Sentry from '@sentry/react';
import { glassStyles } from '@/lib/design';
import { PageBlock } from './PageBlock';
import { RegionSelector, cropImageRegion } from './RegionSelector';
import { PreviewModal } from './PreviewModal';
import {
  type DigitalPageBlock,
  type FormattedLine,
  type ScannedPageData,
  type PendingExtraction,
  type RegionSelection,
  generateBlockId,
  createEmptyBlock,
  splitBlockAtLine,
  splitBlockAtCursor,
  mergeBlocks,
  textToLines,
  linesToText,
  inferInsertionIndex,
} from './types';
import {
  ZOOM_MIN,
  ZOOM_MAX,
  ZOOM_STEP,
  clampZoom,
  matchKeyboardShortcut,
  renumberBlocks,
  deleteBlock,
  extractIndentPattern,
  applyIndentPattern,
  applyIndentPatternToAllBlocks,
  insertExtractedText,
  moveInsertionIndex,
  formatPageIndicator,
} from './block-editor-helpers';

// ============================================================================
// Types
// ============================================================================

interface BlockEditorProps {
  bookId: string;
  bookTitle: string;
  bookAuthor: string | null;
  scannedPages: ScannedPageData[];
  initialBlocks: DigitalPageBlock[];
  onSave: (blocks: DigitalPageBlock[]) => Promise<void>;
  onPublish: (blocks: DigitalPageBlock[]) => Promise<void>;
  onClose?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function BlockEditor({
  bookId,
  bookTitle,
  bookAuthor,
  scannedPages,
  initialBlocks,
  onSave,
  onPublish,
  onClose,
}: BlockEditorProps) {
  // ============================================================================
  // State
  // ============================================================================
  
  const [blocks, setBlocks] = useState<DigitalPageBlock[]>(initialBlocks);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(
    initialBlocks[0]?.id || null
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [regionSelectMode, setRegionSelectMode] = useState(false);
  const [pendingExtraction, setPendingExtraction] = useState<PendingExtraction | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Undo/redo: store snapshots of blocks (cap size, clear future on new change)
  const HISTORY_MAX = 50;
  const historyPastRef = useRef<DigitalPageBlock[][]>([]);
  const historyFutureRef = useRef<DigitalPageBlock[][]>([]);
  const blocksRef = useRef<DigitalPageBlock[]>(blocks);
  blocksRef.current = blocks;

  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);

  const pushHistory = useCallback(() => {
    const snapshot = JSON.parse(JSON.stringify(blocksRef.current)) as DigitalPageBlock[];
    historyPastRef.current.push(snapshot);
    if (historyPastRef.current.length > HISTORY_MAX) historyPastRef.current.shift();
    historyFutureRef.current = [];
    setUndoCount(historyPastRef.current.length);
    setRedoCount(0);
  }, []);

  const undo = useCallback(() => {
    if (historyPastRef.current.length === 0) return;
    const prev = historyPastRef.current.pop()!;
    historyFutureRef.current.push(JSON.parse(JSON.stringify(blocksRef.current)) as DigitalPageBlock[]);
    setBlocks(prev);
    setUndoCount(historyPastRef.current.length);
    setRedoCount(historyFutureRef.current.length);
    setIsDirty(true);
  }, []);

  const redo = useCallback(() => {
    if (historyFutureRef.current.length === 0) return;
    const next = historyFutureRef.current.pop()!;
    historyPastRef.current.push(JSON.parse(JSON.stringify(blocksRef.current)) as DigitalPageBlock[]);
    setBlocks(next);
    setRedoCount(historyFutureRef.current.length);
    setUndoCount(historyPastRef.current.length);
    setIsDirty(true);
  }, []);

  // Zoom & pan state for image cross-reference
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement>(null);
  // Zoom constants imported from block-editor-helpers
  const editorRootRef = useRef<HTMLDivElement>(null);

  // Refs for touch handlers (read current zoom/pan without stale closures)
  const zoomLevelRef = useRef(zoomLevel);
  const panOffsetRef = useRef(panOffset);
  zoomLevelRef.current = zoomLevel;
  panOffsetRef.current = panOffset;

  // Undo/redo keyboard shortcuts (when focus is in editor). Key check is case-insensitive so Shift+Z works for redo.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!editorRootRef.current?.contains(document.activeElement)) return;
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);
  
  // Format painter state - stores indent PATTERN (array) for poetry/rhyme schemes
  // Children's books often have A-B-A-B patterns that should be copied together
  const [copiedFormat, setCopiedFormat] = useState<{ indentPattern: number[] } | null>(null);

  // Current scanned page
  const currentScannedPage = scannedPages[currentPageIndex] || null;
  const totalScannedPages = scannedPages.length;

  // ============================================================================
  // Drag and Drop
  // ============================================================================

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required to start drag
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      pushHistory();
      setBlocks(prev => {
        const oldIndex = prev.findIndex(b => b.id === active.id);
        const newIndex = prev.findIndex(b => b.id === over.id);

        const reordered = arrayMove(prev, oldIndex, newIndex);

        return renumberBlocks(reordered);
      });
      setIsDirty(true);
    }
  }, [pushHistory]);

  // ============================================================================
  // Block Operations
  // ============================================================================

  const handleBlockChange = useCallback((blockId: string, newLines: FormattedLine[]) => {
    pushHistory();
    setBlocks(prev => prev.map(block =>
      block.id === blockId ? { ...block, lines: newLines } : block
    ));
    setIsDirty(true);
  }, [pushHistory]);

  const handleBlockDelete = useCallback((blockId: string) => {
    pushHistory();
    setBlocks(prev => {
      const result = deleteBlock(prev, blockId);
      return result;
    });
    setIsDirty(true);
  }, [pushHistory]);

  const handleSplitBlock = useCallback((blockId: string, lineIndex: number, offsetInLine?: number) => {
    pushHistory();
    setBlocks(prev => {
      const index = prev.findIndex(b => b.id === blockId);
      if (index === -1) return prev;

      const block = prev[index];
      const [first, second] =
        typeof offsetInLine === 'number'
          ? splitBlockAtCursor(block, lineIndex, offsetInLine)
          : splitBlockAtLine(block, lineIndex);

      const newBlocks = [
        ...prev.slice(0, index),
        first,
        second,
        ...prev.slice(index + 1),
      ];

      return renumberBlocks(newBlocks);
    });
    setIsDirty(true);
  }, [pushHistory]);

  const handleAddBlock = useCallback(() => {
    pushHistory();
    const newBlock = createEmptyBlock(
      blocks.length + 1,
      currentScannedPage ? [currentScannedPage.id] : []
    );
    setBlocks(prev => [...prev, newBlock]);
    setSelectedBlockId(newBlock.id);
    setIsDirty(true);
  }, [blocks.length, currentScannedPage, pushHistory]);

  // ============================================================================
  // Region OCR
  // ============================================================================

  const handleRegionSelected = useCallback(async (region: RegionSelection) => {
    setRegionSelectMode(false);
    
    if (!currentScannedPage?.imageUrl && !currentScannedPage?.enhancedImageUrl) {
      setError('No image available for OCR');
      return;
    }

    const imageUrl = currentScannedPage.enhancedImageUrl || currentScannedPage.imageUrl || '';
    
    // Find current block's line count for insertion inference
    const currentBlock = blocks.find(b => b.id === selectedBlockId);
    const lineCount = currentBlock?.lines.length || 1;
    const inferredIndex = inferInsertionIndex(region, lineCount);

    // Set pending state
    setPendingExtraction({
      region,
      extractedText: null,
      inferredInsertIndex: inferredIndex,
      status: 'extracting',
    });

    try {
      // Crop the image region
      const croppedBase64 = await cropImageRegion(imageUrl, region);
      
      // Call AI Vision for OCR
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-vision`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            task: 'extract-page-text',
            image: croppedBase64,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('OCR request failed');
      }

      const result = await response.json();
      const extractedText = result.text?.trim() || '';

      if (!extractedText) {
        setPendingExtraction(prev => prev ? {
          ...prev,
          status: 'error',
          error: 'No text found in selected region',
        } : null);
        return;
      }

      setPendingExtraction(prev => prev ? {
        ...prev,
        extractedText,
        status: 'ready',
      } : null);

    } catch (err) {
      console.error('[BlockEditor] Region OCR failed:', err);
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'region_ocr' } });
      setPendingExtraction(prev => prev ? {
        ...prev,
        status: 'error',
        error: 'Failed to extract text from region',
      } : null);
    }
  }, [currentScannedPage, blocks, selectedBlockId]);

  const handleAcceptExtraction = useCallback(() => {
    if (!pendingExtraction?.extractedText || !selectedBlockId) return;
    pushHistory();
    setBlocks(prev => prev.map(block => {
      if (block.id !== selectedBlockId) return block;

      const newLines = insertExtractedText(
        block.lines,
        pendingExtraction.extractedText!,
        pendingExtraction.inferredInsertIndex
      );

      return { ...block, lines: newLines };
    }));

    setPendingExtraction(null);
    setIsDirty(true);
    setSuccessMessage('Text inserted');
    setTimeout(() => setSuccessMessage(null), 2000);
  }, [pendingExtraction, selectedBlockId, pushHistory]);

  const handleRejectExtraction = useCallback(() => {
    setPendingExtraction(null);
  }, []);

  const handleMoveExtraction = useCallback((direction: 'up' | 'down') => {
    setPendingExtraction(prev => {
      if (!prev) return null;
      const currentBlock = blocks.find(b => b.id === selectedBlockId);
      const maxIndex = currentBlock?.lines.length || 1;
      const newIndex = moveInsertionIndex(prev.inferredInsertIndex, direction, maxIndex);
      return { ...prev, inferredInsertIndex: newIndex };
    });
  }, [blocks, selectedBlockId]);

  // ============================================================================
  // Format Painter
  // ============================================================================

  const handleCopyFormat = useCallback(() => {
    const selectedBlock = blocks.find(b => b.id === selectedBlockId);
    if (!selectedBlock || selectedBlock.lines.length === 0) return;

    const indentPattern = extractIndentPattern(selectedBlock.lines);
    if (!indentPattern) return;
    setCopiedFormat({ indentPattern });
    setSuccessMessage(`Pattern copied (${indentPattern.length} lines)`);
    setTimeout(() => setSuccessMessage(null), 2000);
  }, [blocks, selectedBlockId]);

  const handleApplyFormat = useCallback(() => {
    if (!copiedFormat || !selectedBlockId) return;
    pushHistory();
    setBlocks(prev => prev.map(block => {
      if (block.id !== selectedBlockId) return block;
      return { ...block, lines: applyIndentPattern(block.lines, copiedFormat.indentPattern) };
    }));

    setIsDirty(true);
    setSuccessMessage(`Pattern applied to first ${copiedFormat.indentPattern.length} lines`);
    setTimeout(() => setSuccessMessage(null), 2000);
  }, [copiedFormat, selectedBlockId, pushHistory]);

  // Apply format pattern to all blocks (bulk operation for consistent formatting)
  const handleApplyFormatToAll = useCallback(() => {
    if (!copiedFormat) return;
    pushHistory();
    setBlocks(prev => applyIndentPatternToAllBlocks(prev, copiedFormat.indentPattern));

    setIsDirty(true);
    setSuccessMessage(`Pattern applied to first ${copiedFormat.indentPattern.length} lines on all pages`);
    setTimeout(() => setSuccessMessage(null), 2000);
  }, [copiedFormat, pushHistory]);

  // ============================================================================
  // Zoom & Pan Controls (Button + Trackpad Gesture Support)
  // ============================================================================

  const handleZoomIn = useCallback(() => {
    setZoomLevel(prev => Math.min(ZOOM_MAX, prev + ZOOM_STEP));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel(prev => Math.max(ZOOM_MIN, prev - ZOOM_STEP));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  // Trackpad gesture handler: pinch-to-zoom + two-finger pan
  // CRITICAL: Must use native event listener with { passive: false } to prevent browser zoom
  // React's onWheel is passive by default and cannot preventDefault()
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;
    
    const handleWheel = (e: WheelEvent) => {
      // Prevent default browser zoom and page scroll
      e.preventDefault();
      e.stopPropagation();
      
      if (e.ctrlKey || e.metaKey) {
        // Pinch-to-zoom gesture (ctrlKey is set by trackpad pinch)
        const delta = -e.deltaY * 0.01; // Normalize delta for smooth zoom
        setZoomLevel(prev => {
          const newZoom = clampZoom(prev + delta);
          return Math.round(newZoom * 100) / 100; // Round to 2 decimal places
        });
      } else {
        // Two-finger scroll = pan (only when zoomed in)
        setZoomLevel(currentZoom => {
          if (currentZoom > 1) {
            setPanOffset(prev => ({
              x: prev.x - e.deltaX,
              y: prev.y - e.deltaY,
            }));
          }
          return currentZoom;
        });
      }
    };
    
    // MUST be { passive: false } to allow preventDefault() on wheel events
    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, []);

  // Touch zoom & pan for mobile/tablet (pinch-to-zoom, one-finger pan when zoomed)
  useEffect(() => {
    const container = imageContainerRef.current;
    if (!container) return;

    type TouchState =
      | {
          type: 'pinch';
          pinchStartDistance: number;
          pinchStartCenterX: number;
          pinchStartCenterY: number;
          zoomStart: number;
          panStartOffsetX: number;
          panStartOffsetY: number;
        }
      | {
          type: 'pan';
          panStartX: number;
          panStartY: number;
          panStartOffsetX: number;
          panStartOffsetY: number;
        };
    let touchState: TouchState | null = null;

    const getDistance = (a: Touch, b: Touch) =>
      Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    const getCenter = (a: Touch, b: Touch) => ({
      x: (a.clientX + b.clientX) / 2,
      y: (a.clientY + b.clientY) / 2,
    });

    const handleTouchStart = (e: TouchEvent) => {
      const touches = e.touches;
      if (touches.length === 2) {
        e.preventDefault();
        const zoomStart = zoomLevelRef.current;
        const pan = panOffsetRef.current;
        const center = getCenter(touches[0], touches[1]);
        touchState = {
          type: 'pinch',
          pinchStartDistance: getDistance(touches[0], touches[1]),
          pinchStartCenterX: center.x,
          pinchStartCenterY: center.y,
          zoomStart,
          panStartOffsetX: pan.x,
          panStartOffsetY: pan.y,
        };
      } else if (
        touches.length === 1 &&
        zoomLevelRef.current > 1 &&
        !regionSelectMode
      ) {
        touchState = {
          type: 'pan',
          panStartX: touches[0].clientX,
          panStartY: touches[0].clientY,
          panStartOffsetX: panOffsetRef.current.x,
          panStartOffsetY: panOffsetRef.current.y,
        };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touches = e.touches;
      if (touchState?.type === 'pinch' && touches.length === 2) {
        e.preventDefault();
        const distance = getDistance(touches[0], touches[1]);
        const center = getCenter(touches[0], touches[1]);
        const scale = distance / touchState.pinchStartDistance;
        const newZoom = clampZoom(touchState.zoomStart * scale);
        const newZoomRounded = Math.round(newZoom * 100) / 100;
        setZoomLevel(newZoomRounded);
        setPanOffset({
          x: touchState.panStartOffsetX + (center.x - touchState.pinchStartCenterX),
          y: touchState.panStartOffsetY + (center.y - touchState.pinchStartCenterY),
        });
      } else if (touchState?.type === 'pan' && touches.length === 1) {
        e.preventDefault();
        setPanOffset({
          x: touchState.panStartOffsetX + (touches[0].clientX - touchState.panStartX),
          y: touchState.panStartOffsetY + (touches[0].clientY - touchState.panStartY),
        });
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) touchState = null;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    container.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      container.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [regionSelectMode]);

  // Reset pan when zoom returns to 1x
  useEffect(() => {
    if (zoomLevel <= 1) {
      setPanOffset({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  // ============================================================================
  // Save & Publish
  // ============================================================================

  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    
    try {
      await onSave(blocks);
      setIsDirty(false);
      setSuccessMessage('Saved');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err) {
      console.error('[BlockEditor] Save failed:', err);
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'save_content' }, extra: { bookId } });
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [blocks, onSave]);

  const handlePublish = useCallback(async () => {
    setPublishing(true);
    setError(null);
    
    try {
      await onPublish(blocks);
      setSuccessMessage('Submitted for review!');
    } catch (err) {
      console.error('[BlockEditor] Submit failed:', err);
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'submit_book' }, extra: { bookId } });
      setError('Failed to submit');
    } finally {
      setPublishing(false);
    }
  }, [blocks, onPublish]);

  // Auto-save on Cmd+S
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) { e.preventDefault(); }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) return;
    const timer = setTimeout(() => {
      handleSave();
    }, 60_000);
    return () => clearTimeout(timer);
  }, [isDirty, handleSave]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div ref={editorRootRef} className="h-full flex flex-col bg-black">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#D4D4D4]" style={glassStyles.card}>
        <div className="flex items-center gap-4">
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-black/[0.05] transition-colors text-[#3D3D3D] hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div>
            <h1 className="text-lg font-semibold text-[#1A1A1A]">{bookTitle}</h1>
            <p className="text-sm text-[#888888]">
              {bookAuthor || 'Unknown Author'} • {blocks.length} digital pages
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Status Messages */}
          {successMessage && (
            <div className="flex items-center gap-2 text-[#30D158] text-sm">
              <CheckCircle className="w-4 h-4" />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-[#FF453A] text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}
          {isDirty && (
            <span className="text-xs text-[#888888]">Unsaved changes</span>
          )}
          
          {/* Undo / Redo */}
          <button
            onClick={undo}
            disabled={undoCount === 0}
            className="p-2 rounded-lg hover:bg-black/[0.05] transition-colors text-[#3D3D3D] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            title="Undo (⌘Z / Ctrl+Z)"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button
            onClick={redo}
            disabled={redoCount === 0}
            className="p-2 rounded-lg hover:bg-black/[0.05] transition-colors text-[#3D3D3D] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            title="Redo (⌘⇧Z / Ctrl+Shift+Z)"
          >
            <RotateCw className="w-5 h-5" />
          </button>
          
          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] transition-all disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          
          {/* Preview & Finish Button - Opens full preview, then submits for SuperAdmin review */}
          <button
            onClick={() => setShowFullPreview(true)}
            disabled={publishing || blocks.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#30D158] text-[#1A1A1A] font-medium transition-all hover:brightness-110 disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            Preview &amp; Finish
          </button>
        </div>
      </div>

      {/* Main Content - Edit Mode (Split View) */}
      <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Scanned Image */}
            <div className="w-1/2 p-4 border-r border-[#D4D4D4] flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#1A1A1A]">Scanned Image</h3>
                <div className="flex items-center gap-2">
                  {/* Region Select Toggle */}
                  <button
                    onClick={() => setRegionSelectMode(!regionSelectMode)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all ${
                      regionSelectMode 
                        ? 'bg-[#0A84FF] text-white' 
                        : 'bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05]'
                    }`}
                  >
                    <Scan className="w-4 h-4" />
                    {regionSelectMode ? 'Cancel' : 'Select Region'}
                  </button>
                  
                  {/* Zoom Controls - also supports trackpad pinch-to-zoom */}
                  <div className="flex items-center gap-1 border-l border-[#D4D4D4] pl-2">
                    <button
                      onClick={handleZoomOut}
                      disabled={zoomLevel <= ZOOM_MIN}
                      className="p-1.5 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Zoom out (or pinch trackpad)"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleZoomReset}
                      className={`px-2 py-1 rounded-lg text-xs min-w-[50px] transition-colors ${
                        zoomLevel !== 1 
                          ? 'bg-[#0A84FF]/20 text-[#0A84FF] hover:bg-[#0A84FF]/30' 
                          : 'bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05]'
                      }`}
                      title="Reset zoom & pan (click to return to 100%)"
                    >
                      {Math.round(zoomLevel * 100)}%
                    </button>
                    <button
                      onClick={handleZoomIn}
                      disabled={zoomLevel >= ZOOM_MAX}
                      className="p-1.5 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Zoom in (or pinch trackpad)"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Page Navigation */}
                  <div className="flex items-center gap-1 border-l border-[#D4D4D4] pl-2">
                    <button
                      onClick={() => setCurrentPageIndex(i => Math.max(0, i - 1))}
                      disabled={currentPageIndex === 0 || totalScannedPages === 0}
                      className="p-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm text-[#888888] min-w-[60px] text-center">
                      {formatPageIndicator(currentPageIndex, totalScannedPages)}
                    </span>
                    <button
                      onClick={() => setCurrentPageIndex(i => Math.min(totalScannedPages - 1, i + 1))}
                      disabled={currentPageIndex >= totalScannedPages - 1 || totalScannedPages === 0}
                      className="p-2 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
              
              <div 
                ref={imageContainerRef}
                className="flex-1 bg-black/[0.03] rounded-xl overflow-hidden relative touch-none"
                style={{ cursor: zoomLevel > 1 ? 'grab' : 'default' }}
              >
                {currentScannedPage ? (
                  <div 
                    className="w-full h-full"
                    style={{ 
                      transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                      transformOrigin: 'top left',
                      transition: 'transform 0.05s ease-out',
                    }}
                  >
                    <RegionSelector
                      imageUrl={currentScannedPage.enhancedImageUrl || currentScannedPage.imageUrl || ''}
                      pageIndex={currentPageIndex}
                      isActive={regionSelectMode}
                      onRegionSelected={handleRegionSelected}
                      onCancel={() => setRegionSelectMode(false)}
                    />
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[#D4D4D4]">
                    No image available
                  </div>
                )}
                
                {/* Zoom hint overlay */}
                {zoomLevel === 1 && (
                  <div className="absolute bottom-2 left-2 text-[10px] text-[#D4D4D4] pointer-events-none">
                    Pinch or scroll to zoom • Drag to pan when zoomed
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Block Editor */}
            <div className="w-1/2 p-4 flex flex-col">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-[#1A1A1A]">Digital Pages</h3>
                <div className="flex items-center gap-2">
                  {/* Format Painter - collapsed into single dropdown-style button */}
                  <div className="relative group">
                    <button
                      onClick={copiedFormat ? handleApplyFormat : handleCopyFormat}
                      disabled={!selectedBlockId}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm transition-all ${
                        copiedFormat 
                          ? 'bg-[#FF9F0A]/20 text-[#FF9F0A]' 
                          : 'bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] disabled:opacity-30'
                      }`}
                      title={copiedFormat 
                        ? 'Click to apply pattern to selected page · Right-click for options' 
                        : 'Copy indent pattern from selected page'
                      }
                      onContextMenu={(e) => {
                        e.preventDefault();
                        if (copiedFormat) handleApplyFormatToAll();
                      }}
                    >
                      <Paintbrush className="w-3.5 h-3.5" />
                      {copiedFormat ? 'Paste' : 'Format'}
                    </button>
                  </div>
                  <button
                    onClick={handleAddBlock}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-black/[0.03] text-[#3D3D3D] hover:bg-black/[0.05] text-sm transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    Add Page
                  </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-auto space-y-3">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={blocks.map(b => b.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {blocks.map((block) => (
                      <PageBlock
                        key={block.id}
                        block={block}
                        isSelected={block.id === selectedBlockId}
                        pendingExtraction={
                          block.id === selectedBlockId ? pendingExtraction : null
                        }
                        onSelect={() => setSelectedBlockId(block.id)}
                        onChange={(lines) => handleBlockChange(block.id, lines)}
                        onDelete={() => handleBlockDelete(block.id)}
                        onSplitAtCursor={(lineIndex, offsetInLine) => handleSplitBlock(block.id, lineIndex, offsetInLine)}
                        onAcceptExtraction={handleAcceptExtraction}
                        onRejectExtraction={handleRejectExtraction}
                        onMoveExtraction={handleMoveExtraction}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              </div>
              
              {/* Footer hint */}
              <div className="mt-4 pt-4 border-t border-[#D4D4D4] text-xs text-[#D4D4D4]">
                Drag to reorder • Tab/Shift+Tab indent • ⌘/Ctrl+Enter split • ⌘/Ctrl+Z undo/redo • ⌘/Ctrl+S save
              </div>
            </div>
      </div>
      
      {/* Full Preview Modal - Turn Art + Turn Color rendering */}
      <PreviewModal
        isOpen={showFullPreview}
        onClose={() => setShowFullPreview(false)}
        onPublish={async () => {
          await handlePublish();
          setShowFullPreview(false);
        }}
        bookId={bookId}
        bookTitle={bookTitle}
        bookAuthor={bookAuthor}
        blocks={blocks}
      />
    </div>
  );
}

export default BlockEditor;
